import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DiscoverItem } from "../types";
import { useFilteredItems } from "../hooks/use-filtered-items";
import {
  useDiscoverActions,
  useDiscoverCities,
  useDiscoverContext,
} from "../state/discover-context";
import { DiscoverCard } from "./discover-card";

import { Icon } from "@majourneys/icons";
const MAX_PER_SHELF = 12;
/** Hide a shelf with too little to justify a whole row. */
const MIN_PER_SHELF = 3;
/** One card plus its gap, so a press moves the rail by exactly one card. */
const SHELF_STEP = 252;

interface Shelf {
  readonly id: string;
  readonly title: string;
  readonly items: readonly DiscoverItem[];
  /** Jump action for the "See all" affordance (omit to hide it). */
  readonly onSeeAll?: () => void;
}

/** Pull the leading integer out of a price label ("MAD 250" → 250). */
function parsePrice(label: string | null): number | null {
  if (!label) return null;
  const m = label.replace(/[, ]/g, "").match(/(\d+)/);
  return m ? Number(m[1]) : null;
}

/**
 * Editorial "shelves" view for the Discover landing — replaces the flat grid
 * on the default "For you" tab (no search, map zoomed out). Each shelf is a
 * horizontal rail of real {@link DiscoverCard}s grouped by segment / theme,
 * built from the already city- and visibility-scoped filtered set. Empty or
 * thin shelves drop out, and each rail's "See all" jumps to the matching tab.
 */
export function DiscoverShelves() {
  const { t, i18n } = useTranslation();
  const items = useFilteredItems();
  const { state } = useDiscoverContext();
  const cities = useDiscoverCities();
  const { setTab, setCity } = useDiscoverActions();

  const shelves = useMemo<Shelf[]>(() => {
    const topByMentions = (list: DiscoverItem[]) =>
      [...list].sort((a, b) => b.mentions - a.mentions).slice(0, MAX_PER_SHELF);
    const seg = (s: DiscoverItem["segment"]) =>
      topByMentions(items.filter((i) => i.segment === s));

    const out: Shelf[] = [];
    const push = (
      id: string,
      title: string,
      list: readonly DiscoverItem[],
      onSeeAll?: () => void,
    ) => {
      if (list.length >= MIN_PER_SHELF) out.push({ id, title, items: list, onSeeAll });
    };

    push("attractions", t("discover.shelf.attractions"), seg("locations"), () => setTab("attractions"));
    push("eat", t("discover.shelf.where_to_eat"), seg("restaurants"), () => setTab("restaurants"));
    push("todo", t("discover.shelf.things_to_do"), seg("activities"), () => setTab("things-to-do"));
    push("stay", t("discover.shelf.places_to_stay"), seg("stays"), () => setTab("stays"));
    push("spa", t("discover.shelf.spas"), seg("spas"), () => setTab("things-to-do"));
    push("golf", t("discover.shelf.golf"), seg("golf"), () => setTab("things-to-do"));
    push("beach-clubs", t("discover.shelf.beach_clubs"), seg("beach-clubs"), () =>
      setTab("things-to-do"),
    );
    push("nightlife", t("discover.shelf.nightlife"), seg("nightlife"), () =>
      setTab("things-to-do"),
    );

    const budget = items
      .filter((i) => {
        const p = parsePrice(i.priceLabel);
        return p !== null && p <= 300;
      })
      .sort((a, b) => (parsePrice(a.priceLabel) ?? 0) - (parsePrice(b.priceLabel) ?? 0))
      .slice(0, MAX_PER_SHELF);
    push("budget", t("discover.shelf.budget"), budget);

    // "Best of {city}" only makes sense while browsing all cities; once a city
    // is selected every shelf is already that city's.
    if (!state.selectedCitySlug) {
      const count = new Map<string, number>();
      for (const i of items) if (i.citySlug) count.set(i.citySlug, (count.get(i.citySlug) ?? 0) + 1);
      const top = [...count.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
      if (top) {
        const cityName = cities.find((c) => c.slug === top)?.name ?? top;
        push(
          "best",
          t("discover.shelf.best_of", { city: cityName }),
          topByMentions(items.filter((i) => i.citySlug === top)),
          () => setCity(top),
        );
      }
    }

    return out;
  }, [items, state.selectedCitySlug, cities, t, setTab, setCity]);

  // Thin city / filter combo where nothing groups into a full rail — fall back
  // to the plain grid so the panel is never blank.
  if (shelves.length === 0) {
    return (
      <div className="mj-discover-browser__grid">
        {items.map((item) => (
          <DiscoverCard key={item.id} item={item} />
        ))}
      </div>
    );
  }

  const seeAllIcon = i18n.language === "ar" ? "ri-arrow-left-line" : "ri-arrow-right-line";

  return (
    <div className="mj-discover-shelves">
      {shelves.map((shelf) => (
        <section key={shelf.id} className="mj-discover-shelf" aria-label={shelf.title}>
          <header className="mj-discover-shelf__head">
            <h3 className="mj-discover-shelf__title">{shelf.title}</h3>
            {shelf.onSeeAll && (
              <button
                type="button"
                className="mj-press mj-discover-shelf__seeall"
                onClick={shelf.onSeeAll}
              >
                {t("discover.shelf.see_all")}
                <Icon name={seeAllIcon} aria-hidden="true" />
              </button>
            )}
          </header>
          <ShelfRow items={shelf.items} />
        </section>
      ))}
    </div>
  );
}

/**
 * One rail of cards with its two round scroll buttons.
 *
 * The rail hides its scrollbar (owner rule: no visible horizontal scrollbar
 * anywhere), so without the buttons a row that runs off the edge has nothing
 * saying so, and inventory we paid to publish stays invisible to anyone who
 * does not think to swipe. Each button hides itself at its own end of the
 * rail rather than unmounting, so the pair never reflows under the cursor.
 */
function ShelfRow({ items }: { items: readonly DiscoverItem[] }) {
  const { t, i18n } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    /* `scrollLeft` is negative in a right-to-left container in some engines,
       so distance travelled is the magnitude, and the far edge is compared
       against the measured maximum rather than assumed to be zero. */
    const pos = Math.abs(el.scrollLeft);
    const start = pos > 4;
    const end = max > 4 && pos < max - 4;
    /* Runs on every scroll event: keep the same object when nothing changed,
       or each swipe re-renders the whole row of cards every frame. */
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    /* A row can gain or lose overflow long after mount: images settle, a
       translation rewraps a title, a filter changes the card count. */
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      for (const child of Array.from(el.children)) ro.observe(child);
    }
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [measure]);

  const nudge = (towardEnd: boolean) => {
    const el = ref.current;
    if (!el) return;
    const rtl = getComputedStyle(el).direction === "rtl";
    /* In a right-to-left row, moving toward the end means decreasing
       scrollLeft, so the sign follows the writing direction. */
    const sign = towardEnd === rtl ? -1 : 1;
    el.scrollBy({ left: sign * SHELF_STEP, behavior: "smooth" });
  };

  /* Logical properties place the buttons, but a glyph does not flip on its
     own. The codebase swaps the icon off the language rather than using an
     `rtl:` variant, so this follows that. */
  const rtlLang = i18n.language === "ar";

  return (
    <div className="mj-discover-shelf__rail">
      <div ref={ref} className="mj-discover-shelf__row mj-no-scrollbar">
        {items.map((item) => (
          <div key={item.id} className="mj-discover-shelf__item">
            <DiscoverCard item={item} />
          </div>
        ))}
      </div>
      <button
        type="button"
        className="mj-press mj-discover-shelf__nav mj-discover-shelf__nav--start"
        aria-label={t("common.scroll_back", { defaultValue: "Scroll back" })}
        disabled={!edges.start}
        onClick={() => nudge(false)}
      >
        <Icon name={rtlLang ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} aria-hidden="true" />
      </button>
      <button
        type="button"
        className="mj-press mj-discover-shelf__nav mj-discover-shelf__nav--end"
        aria-label={t("common.scroll_forward", { defaultValue: "Scroll forward" })}
        disabled={!edges.end}
        onClick={() => nudge(true)}
      >
        <Icon name={rtlLang ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"} aria-hidden="true" />
      </button>
    </div>
  );
}
